import React, { useState } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import StagingNotice, { isStaging } from '../components/common/StagingNotice'
import { authService } from '../services/authService'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showStagingNotice, setShowStagingNotice] = useState(isStaging)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.username || !formData.password) {
      setError('กรุณากรอกชื่อผู้ใช้และรหัสผ่าน')
      return
    }

    try {
      setLoading(true)
      setError(null)

      await authService.login(formData.username, formData.password)

      // Redirect to dashboard
      window.location.href = '/'
    } catch (err: any) {
      console.error('Login error:', err)
      if (err.response?.status === 401) {
        setError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
      } else {
        setError('เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCloseError = () => {
    setError(null)
  }

  if (showStagingNotice) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F8FAFC',
          p: 2,
          boxSizing: 'border-box',
        }}
      >
        <StagingNotice onContinue={() => setShowStagingNotice(false)} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F8FAFC',
      }}
    >
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            py: 2,
            px: 4,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img
              src="/images/logo-teamcm.webp"
              alt="TEAM•OM"
              style={{ height: 120, marginBottom: 8 }}
            />
            <Typography
              variant="h5"
              sx={{ mb: 2, color: '#114776', fontWeight: 500 }}
            >
              {isStaging ? 'เข้าสู่ระบบ Staging' : 'เข้าสู่ระบบ'}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                บัญชีผู้ใช้
              </Typography>
              <TextField
                fullWidth
                placeholder="บัญชีผู้ใช้"
                variant="outlined"
                margin="normal"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                disabled={loading}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                รหัสผ่าน
              </Typography>
              <TextField
                fullWidth
                placeholder="รหัสผ่าน"
                type="password"
                variant="outlined"
                margin="normal"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                disabled={loading}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              m: 2,
              display: 'flex',
              textAlign: 'center',
              gap: 4,
            }}
          >
            <Button
              variant="text"
              sx={{
                p: 0,
                color: '#114776',
                fontWeight: 500,
                textDecoration: 'underline',
              }}
              onClick={() => {
                window.open(
                  './manual/Manual_Book_TEAM_CM_Check-in_Check-out_website_application.pdf',
                  '_blank',
                )
              }}
            >
              คู่มือการใช้งานระบบ
            </Button>

            <Button
              variant="text"
              sx={{
                p: 0,
                color: '#114776',
                fontWeight: 500,
                textDecoration: 'underline',
              }}
              onClick={() => {
                window.open(
                  './manual/Manual_Book_TEAM_CM_Check-in_Check-out_mobile_application.pdf',
                  '_blank',
                )
              }}
            >
              คู่มือการใช้งาน Mobile Application
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Login
